import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import {
  Tags, Plus, Search, Download, Upload, Trash2, RotateCcw, Users, Save,
} from "lucide-react";
import { api, apiError } from "@/lib/api";
import { inr } from "@/lib/format";
import { useCanEdit } from "@/context/AuthContext";
import { CardList, DataCard, TableWrap } from "@/components/DataCards";
import { downloadFile } from "@/components/Pagination";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";

const blankList = () => ({
  name: "", code: "", description: "", adjustment_percent: 0,
  valid_from: "", valid_to: "", is_active: true,
});

const SOURCE_LABEL = {
  PRICE_LIST: "Set on this list",
  ADJUSTMENT: "From the % adjustment",
  DEFAULT: "Item's own price",
};

/** Reads as a discount or a mark-up, never a bare signed number. */
export function adjustmentLabel(pct) {
  const n = Number(pct) || 0;
  if (!n) return "No blanket adjustment";
  return `${Math.abs(n)}% ${n < 0 ? "off" : "over"} the item price`;
}

export default function PriceLists() {
  const canEdit = useCanEdit();
  const [lists, setLists] = useState([]);
  const [active, setActive] = useState(null);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(blankList());

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/price-lists", { params: { include_inactive: true } });
      setLists(data);
      setActive((cur) => (cur ? data.find((l) => l.id === cur.id) || null : null));
    } catch (e) {
      toast.error(apiError(e));
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const startNew = () => { setEditing(null); setForm(blankList()); setOpen(true); };
  const startEdit = (l) => {
    setEditing(l);
    setForm({ ...blankList(), ...l, adjustment_percent: String(l.adjustment_percent ?? 0) });
    setOpen(true);
  };

  const save = async () => {
    if (!form.name.trim()) { toast.error("Give the price list a name"); return; }
    const payload = { ...form, adjustment_percent: Number(form.adjustment_percent) || 0 };
    try {
      if (editing) await api.patch(`/price-lists/${editing.id}`, payload);
      else await api.post("/price-lists", payload);
      toast.success(editing ? "Price list updated" : "Price list created");
      setOpen(false);
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const remove = async (l) => {
    if (!window.confirm(`Delete "${l.name}" and every rate on it?`)) return;
    try {
      await api.delete(`/price-lists/${l.id}`);
      toast.success("Price list deleted");
      if (active?.id === l.id) setActive(null);
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  if (active) {
    return <RateGrid list={active} canEdit={canEdit} onBack={() => { setActive(null); load(); }} />;
  }

  return (
    <div className="space-y-4" data-testid="price-lists">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <p className="max-w-2xl text-sm text-slate-500">
          Every item has one selling price. A price list overrides it for the customers
          you put on that list — set a rate per item, or one percentage across the whole
          catalogue. The invoice starts at that rate; you can still type over it.
        </p>
        {canEdit && (
          <Button size="sm" onClick={startNew} className="gap-1.5 bg-blue-600 hover:bg-blue-700"
            data-testid="new-price-list">
            <Plus size={15} /> New price list
          </Button>
        )}
      </div>

      {!lists.length ? (
        <div className="rounded-lg border border-dashed border-slate-300 px-6 py-14 text-center"
          data-testid="price-lists-empty">
          <Tags size={26} className="mx-auto text-slate-300" />
          <p className="mt-3 text-sm font-medium text-slate-700">No price lists yet</p>
          <p className="mt-1 text-xs text-slate-500">
            Add one for dealers or distributors, put your customers on it, and their
            invoices price themselves.
          </p>
        </div>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
          {lists.map((l) => (
            <div key={l.id} data-testid={`price-list-${l.id}`}
              className={`rounded-lg border bg-white p-4 ${
                l.in_force ? "border-slate-200" : "border-slate-200 opacity-70"}`}>
              <div className="flex items-start justify-between gap-2">
                <div>
                  <button className="text-left text-sm font-semibold hover:text-blue-700 transition-colors duration-150"
                    onClick={() => setActive(l)} data-testid={`open-price-list-${l.id}`}>
                    {l.name}
                  </button>
                  {l.code && <div className="font-mono text-[11px] text-slate-400">{l.code}</div>}
                </div>
                {!l.in_force && (
                  <span className="rounded bg-slate-100 px-1.5 py-0.5 text-[10px] font-semibold text-slate-500">
                    {l.is_active ? "Out of dates" : "Inactive"}
                  </span>
                )}
              </div>

              <div className="mt-3 text-xs text-slate-600">{adjustmentLabel(l.adjustment_percent)}</div>
              <div className="mt-2 flex items-center gap-4 text-[11px] text-slate-500">
                <span>{l.item_count} item{l.item_count === 1 ? "" : "s"} priced</span>
                <span className="flex items-center gap-1">
                  <Users size={12} /> {l.party_count}
                </span>
              </div>
              {(l.valid_from || l.valid_to) && (
                <div className="mt-1 text-[11px] text-slate-400">
                  {l.valid_from || "any date"} → {l.valid_to || "no end"}
                </div>
              )}

              <div className="mt-3 flex gap-3">
                <button className="text-xs text-blue-700 hover:text-blue-900 transition-colors duration-150"
                  onClick={() => setActive(l)}>Set rates</button>
                {canEdit && (
                  <>
                    <button className="text-xs text-slate-500 hover:text-slate-800 transition-colors duration-150"
                      onClick={() => startEdit(l)} data-testid={`edit-price-list-${l.id}`}>Edit</button>
                    <button className="text-xs text-slate-400 hover:text-rose-600 transition-colors duration-150"
                      onClick={() => remove(l)} data-testid={`delete-price-list-${l.id}`}>Delete</button>
                  </>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg" data-testid="price-list-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? "Edit price list" : "New price list"}</DialogTitle>
            <DialogDescription>
              The percentage is a shortcut for the whole catalogue. Any item you give an
              explicit rate to ignores it.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-1.5 sm:col-span-2">
              <Label className="text-xs">Name</Label>
              <Input value={form.name} placeholder="Dealer" data-testid="price-list-name"
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Code</Label>
              <Input className="font-mono uppercase" value={form.code || ""}
                onChange={(e) => setForm((f) => ({ ...f, code: e.target.value.toUpperCase() }))} />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Adjustment %</Label>
              <Input className="font-mono text-right" value={form.adjustment_percent}
                data-testid="price-list-adjustment"
                onChange={(e) => setForm((f) => ({ ...f, adjustment_percent: e.target.value }))} />
              <p className="text-[11px] text-slate-500">
                −10 for ten percent off, 5 for a five percent mark-up.
              </p>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Valid from</Label>
              <Input type="date" value={form.valid_from || ""}
                onChange={(e) => setForm((f) => ({ ...f, valid_from: e.target.value }))} />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Valid to</Label>
              <Input type="date" value={form.valid_to || ""}
                onChange={(e) => setForm((f) => ({ ...f, valid_to: e.target.value }))} />
            </div>
            <div className="space-y-1.5 sm:col-span-2">
              <Label className="text-xs">Notes</Label>
              <Textarea rows={2} value={form.description || ""}
                onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} />
            </div>
            <div className="flex items-center gap-2 sm:col-span-2">
              <Switch checked={!!form.is_active} data-testid="price-list-active"
                onCheckedChange={(v) => setForm((f) => ({ ...f, is_active: v }))} />
              <span className="text-xs text-slate-600">
                Active — customers on an inactive list fall back to the item price
              </span>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} className="bg-blue-600 hover:bg-blue-700"
              data-testid="price-list-save">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* ------------------------------------------------------------------ rates */
function RateGrid({ list, canEdit, onBack }) {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [onlyPriced, setOnlyPriced] = useState(false);
  const [edits, setEdits] = useState({});
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/price-lists/${list.id}/items`, {
        params: { q: q || undefined, only_priced: onlyPriced || undefined },
      });
      setRows(data);
      setEdits({});
    } catch (e) {
      toast.error(apiError(e));
    }
  }, [list.id, q, onlyPriced]);

  useEffect(() => { load(); }, [load]);

  const dirty = useMemo(() => Object.keys(edits).length, [edits]);

  const saveAll = async () => {
    const entries = Object.entries(edits)
      .filter(([, v]) => String(v).trim() !== "" && !Number.isNaN(Number(v)))
      .map(([item_id, v]) => ({ item_id, rate: Number(v) }));
    if (!entries.length) { toast.error("Nothing to save"); return; }
    setBusy(true);
    try {
      await api.put(`/price-lists/${list.id}/items`, { entries });
      toast.success(`${entries.length} rate${entries.length === 1 ? "" : "s"} saved`);
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
    setBusy(false);
  };

  const clearRate = async (itemId) => {
    try {
      await api.delete(`/price-lists/${list.id}/items/${itemId}`);
      toast.success("Back to the item's own price");
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const importCsv = async (file) => {
    if (!file) return;
    const body = new FormData();
    body.append("file", file);
    try {
      const { data } = await api.post(`/price-lists/${list.id}/import`, body);
      if (data.skipped_count) {
        toast.warning(`${data.applied} applied · ${data.skipped_count} skipped`, {
          description: data.skipped.slice(0, 3).join(" · "),
        });
      } else {
        toast.success(`${data.applied} rate${data.applied === 1 ? "" : "s"} imported`);
      }
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
    if (fileRef.current) fileRef.current.value = "";
  };

  return (
    <div className="space-y-4" data-testid="price-list-rates">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <button onClick={onBack} className="text-xs text-blue-700 hover:text-blue-900 transition-colors duration-150"
            data-testid="price-list-back">← All price lists</button>
          <h2 className="mt-1 font-heading text-lg font-bold">{list.name}</h2>
          <p className="text-xs text-slate-500">
            {adjustmentLabel(list.adjustment_percent)} · {list.party_count} customer
            {list.party_count === 1 ? "" : "s"} on this list
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button size="sm" variant="outline" className="gap-1.5" data-testid="price-list-export"
            onClick={() => downloadFile(`/price-lists/${list.id}/export.csv`, {}, "price-list.csv")}>
            <Download size={14} /> Export
          </Button>
          {canEdit && (
            <>
              <input type="file" accept=".csv" ref={fileRef} className="hidden"
                data-testid="price-list-file"
                onChange={(e) => importCsv(e.target.files?.[0])} />
              <Button size="sm" variant="outline" className="gap-1.5" data-testid="price-list-import"
                onClick={() => fileRef.current?.click()}>
                <Upload size={14} /> Import
              </Button>
            </>
          )}
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <div className="relative min-w-[180px] flex-1">
          <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input className="h-9 pl-8" placeholder="Search item or code" value={q}
            data-testid="price-list-search" onChange={(e) => setQ(e.target.value)} />
        </div>
        <label className="flex items-center gap-2 text-xs text-slate-600">
          <Switch checked={onlyPriced} onCheckedChange={setOnlyPriced}
            data-testid="price-list-only-priced" />
          Only items priced on this list
        </label>
      </div>

      <TableWrap>
        <table className="w-full text-sm" data-testid="price-list-table">
          <thead className="bg-slate-50 text-[11px] uppercase tracking-wide text-slate-500">
            <tr>
              <th className="px-4 py-2.5 text-left font-semibold">Item</th>
              <th className="px-3 py-2.5 text-left font-semibold">Code</th>
              <th className="px-3 py-2.5 text-right font-semibold">Item price</th>
              <th className="px-3 py-2.5 text-right font-semibold">This list pays</th>
              <th className="px-3 py-2.5 text-left font-semibold">Where from</th>
              <th className="px-4 py-2.5 text-right font-semibold"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {rows.map((r) => (
              <tr key={r.item_id} className="hover:bg-slate-50 transition-colors duration-150"
                data-testid={`price-row-${r.item_id}`}>
                <td className="px-4 py-2 font-medium">{r.name}</td>
                <td className="px-3 py-2 font-mono text-[11px] text-slate-500">{r.item_code || "—"}</td>
                <td className="px-3 py-2 text-right font-mono text-xs text-slate-500 tabular-nums">
                  {inr(r.default_rate)}
                </td>
                <td className="px-3 py-2 text-right">
                  {canEdit ? (
                    <Input
                      className="ml-auto h-8 w-28 text-right font-mono text-xs"
                      data-testid={`price-input-${r.item_id}`}
                      value={edits[r.item_id] ?? (r.has_entry ? r.rate : "")}
                      placeholder={String(r.rate)}
                      onChange={(e) => setEdits((s) => ({ ...s, [r.item_id]: e.target.value }))} />
                  ) : (
                    <span className="font-mono tabular-nums">{inr(r.rate)}</span>
                  )}
                </td>
                <td className="px-3 py-2 text-[11px] text-slate-500">{SOURCE_LABEL[r.source]}</td>
                <td className="px-4 py-2 text-right">
                  {canEdit && r.has_entry && (
                    <button className="text-slate-400 hover:text-rose-600 transition-colors duration-150"
                      title="Clear this rate" data-testid={`price-clear-${r.item_id}`}
                      onClick={() => clearRate(r.item_id)}>
                      <RotateCcw size={14} />
                    </button>
                  )}
                </td>
              </tr>
            ))}
            {!rows.length && (
              <tr><td colSpan={6} className="px-4 py-14 text-center text-sm text-slate-500">
                No items match
              </td></tr>
            )}
          </tbody>
        </table>
      </TableWrap>

      <CardList>
        {rows.map((r) => (
          <DataCard key={r.item_id} testid={`price-card-${r.item_id}`}
            title={r.name} subtitle={r.item_code || r.unit_symbol}
            right={<span className="font-mono text-sm font-bold tabular-nums">{inr(r.rate)}</span>}
            rows={[
              ["Item price", inr(r.default_rate)],
              ["Where from", SOURCE_LABEL[r.source]],
            ]}
            actions={canEdit && (
              <Input className="h-8 w-28 text-right font-mono text-xs"
                value={edits[r.item_id] ?? (r.has_entry ? r.rate : "")}
                placeholder={String(r.rate)}
                onChange={(e) => setEdits((s) => ({ ...s, [r.item_id]: e.target.value }))} />
            )} />
        ))}
      </CardList>

      {canEdit && (
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-md border border-slate-200 bg-slate-50 px-4 py-3">
          <div className="text-xs text-slate-600">
            {dirty ? `${dirty} rate${dirty === 1 ? "" : "s"} changed` : "Type a rate to override the item price"}
          </div>
          <Button size="sm" disabled={!dirty || busy} onClick={saveAll}
            className="gap-1.5 bg-blue-600 hover:bg-blue-700" data-testid="price-list-save-rates">
            <Save size={15} /> Save rates
          </Button>
        </div>
      )}
    </div>
  );
}
